import React, { useEffect, useState } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { getTeamMembersData, inviteMember } from '~/redux/actions/user-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION, EMAIL_REGEX } from '~/utils/constants';
// Components
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';
import { USER_ROLE_OPTIONS } from '../roleOptions';

// MUI Components
import Typography from '@mui/material/Typography';
import { Box, CircularProgress, InputLabel, TextField, InputAdornment } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

const InviteModal = (props) => {
  const formId = 'invite-user-form';
  const { openModal, handleCloseModal, updatePagination, onLimitReached } = props;
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleError, setRoleError] = useState('');

  const dispatch = useDispatch();

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .matches(EMAIL_REGEX, MESSAGES.AUTH.INVALID_EMAIL)
      .required(MESSAGES.AUTH.EMAIL_REQUIRED),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: validationSchema,
  });

  const onClose = () => {
    handleCloseModal();
    formik.handleReset();
    resetValidation();
    setSelectedRole(null);
    setRoleError('');
    setLoading(false);
  };

  const invite = async (event) => {
    try {
      setLoading(true);
      event.preventDefault();
      const isValid = validateForm(formik);
      if (!isValid) {
        setLoading(false);
        return;
      }
      if (!selectedRole) {
        setRoleError('Role is required');
        setLoading(false);
        return;
      }
      const roleValue = selectedRole.value;
      const response = await dispatch(
        inviteMember({ email: formik.values.email, role: roleValue })
      );
      if (response.status === 200) {
        response?.data?.message && dispatch(success(response.data.message));
        updatePagination(DEFAULT_PAGINATION);
        dispatch(getTeamMembersData(DEFAULT_PAGINATION));
        onClose();
      } else {
        response?.data?.message &&
          dispatch(
            failure(
              response.data.data?.errors?.length
                ? response.data.data?.errors[0].msg
                : response.data?.message
            )
          );
        handleCloseModal();
      }
    } catch (error) {
      setLoading(false);
      // Handle 418 error specifically for limit reached
      if (error?.response?.status === 418) {
        if (onLimitReached) {
          const errorMessage = error?.response?.data?.message || '';
          onClose();
          onLimitReached(errorMessage);
        }
        return;
      }
      // For other errors, dispatch failure message
      dispatch(
        failure(
          error?.response?.data?.message ||
            error?.message ||
            MESSAGES.GENERAL_ERROR ||
            'An error occurred. Please try again.'
        )
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    formik.touched.email = false;
    setRoleError('');
    setSelectedRole(null);
  }, [openModal]);

  useEffect(() => {
    if (!openModal) {
      // Reset all state when modal closes
      formik.handleReset();
      resetValidation();
      setSelectedRole(null);
      setRoleError('');
      setLoading(false);
    }
  }, [openModal]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title={MESSAGES.ACCOUNT.USERS.INVITE_USER.TITLE}
      className="inviteUserFormModal"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose} disabled={loading}>
            {MESSAGES.ACCOUNT.USERS.INVITE_USER.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton
            variant="primary"
            type="submit"
            form={formId}
            onClick={() => validateForm(formik)}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              MESSAGES.ACCOUNT.USERS.INVITE_USER.SUBMIT_BUTTON
            )}
          </OlcButton>
        </>
      }
    >
      <Box className="modalFormWrapper">
        <form id={formId} onSubmit={invite} noValidate>
          <Box className="modalFlexArea">
            <Box>
              <InputLabel htmlFor="Email Address" className="inputLabel formLabel">
                {MESSAGES.ACCOUNT.USERS.INVITE_USER.EMAIL_LABEL}
                <span className="olcRequired">*</span>
              </InputLabel>
              <TextField
                placeholder={MESSAGES.FORGOT_PASSWORD.EMAIL_PLACEHOLDER}
                fullWidth
                name="email"
                className={
                  formik.errors.email && formik.touched.email
                    ? 'invalid formInput inputField formField'
                    : 'inputField formField'
                }
                onChange={formik.handleChange}
                onBlur={realTimeValidation ? formik.handleBlur : undefined}
                value={formik.values.email}
                autoComplete="off"
                sx={{
                  '& input::placeholder': {
                    color: '#000',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: 'normal',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                  },
                  outline: 'none',
                  '&:focus': {
                    outline: 'none',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                      {formik.values.email && (
                        <IconButton
                          className="cpClearInputBtn"
                          size="small"
                          onClick={() => {
                            formik.setFieldValue('email', '');
                            formik.setFieldTouched('email', true);
                          }}
                          edge="end"
                          sx={{ padding: '4px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {formik.touched.email && formik.errors.email && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {formik.errors.email}
                </Typography>
              )}
            </Box>
            <Box className="olcSelectBox">
              <Typography className="inputLabel formLabel">
                {MESSAGES.ACCOUNT.USERS.INVITE_USER.ROLE_LABEL}
                <span className="olcRequired">*</span>
              </Typography>

              <OlcDropdownSearch
                placeHolderText="Select Role"
                searchFieldPlaceholderText="roles"
                noOptionText="No roles found"
                options={USER_ROLE_OPTIONS}
                selectedValue={selectedRole}
                setSelectedValue={(val) => {
                  setSelectedRole(val);
                  if (roleError) setRoleError('');
                }}
                error={!!roleError}
              />
              {roleError ? (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {roleError}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </form>
      </Box>
    </OlcFormModal>
  );
};

export default InviteModal;
